// pages/blog/outdoor-living-hardscaping.js

import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';

export const metadata = {
  title: 'Outdoor Living, Hardscaping & Landscaping That Transform Your Backyard',
  image: '/blog/outdoor-living-hardscaping.webp',
  date: 'July 1, 2025',
  author: 'Modern Build Team',
  excerpt:
    'Modern pavers, outdoor kitchens, lighting, and landscaping can turn your backyard into a luxurious retreat. Discover how to elevate your outdoor space.',
  category: 'Outdoor Remodeling',
  tags: ['Outdoor Living', 'Hardscaping', 'Landscaping', 'Pavers'],
};

export default function OutdoorLivingHardscaping() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/blog/outdoor-living-hardscaping`} />

        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/outdoor-living-hardscaping`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Outdoor Living Hardscaping Pavers",
                  "item": `https://modernbuildremodel.com/blog/outdoor-living-hardscaping`
                },
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'outdoor-living-hardscaping')),
          }}
        />
      </Head>

      <Header />

      <div className="bg-white">
        <Hero category="outdoorLivingHardscaping" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
            <span className="capitalize text-primary tracking-[3px]">By {metadata.author} / {metadata.date}</span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`Modern Build Home Remodeling post: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none text-md text-black leading-relaxed space-y-6">
            <p><strong>Outdoor living</strong> has evolved from simple patios and lawns into full-scale lifestyle spaces—where comfort, beauty, and function meet.</p>

            <p>With the right combination of <strong>hardscaping</strong>, <strong>landscaping</strong>, and <strong>pavers</strong>, your backyard can become a private resort designed for relaxation, entertaining, and year-round enjoyment.</p>

            <h3 className="font-bold">What Is Hardscaping?</h3>
            <p>Hardscaping refers to all non-living elements in your outdoor space: patios, retaining walls, fire pits, steps, and pergolas. These features give structure and usability to your yard—turning it from empty lawn into livable square footage.</p>

            <h3 className="font-bold">Why Pavers Make the Difference</h3>
            <p>Unlike poured concrete or gravel, pavers offer <strong>durability</strong>, <strong>texture</strong>, and <strong>design flexibility</strong>. Whether you're building a driveway, walkways, or a patio, pavers come in a variety of shapes, colors, and patterns to match your home’s style.</p>

            <p>They’re also <strong>low maintenance</strong> and easy to repair, making them ideal for busy homeowners.</p>

            <h3 className="font-bold">Integrated Outdoor Living Features</h3>
            <p>Want to take your outdoor space to the next level? Consider adding:</p>
            <ul className="list-disc pl-6">
              <li><strong>Outdoor kitchens</strong> with built-in grills and counter space</li>
              <li><strong>Fireplaces or fire pits</strong> for cozy gatherings</li>
              <li><strong>Covered pergolas</strong> or shade structures</li>
              <li><strong>Low-voltage lighting</strong> for ambiance and safety</li>
            </ul>

            <p>These features not only add functionality—they create memorable experiences and make your outdoor area a true extension of your home.</p>

            <h3 className="font-bold">Don’t Forget the Landscaping</h3>
            <p>Lush landscaping softens the hard edges of pavers and walls. Use <strong>drought-tolerant plants</strong>, raised beds, and well-placed greenery to bring your space to life. Landscaping also improves air quality, provides shade, and boosts curb appeal.</p>

            <h3 className="font-bold">Custom Designs for Every Lifestyle</h3>
            <p>No two outdoor spaces—or homeowners—are the same. Whether you want a sleek modern lounge or a rustic, family-friendly yard, <strong>Modern Build</strong> designs and installs every project with your lifestyle and budget in mind.</p>

            <p className="mt-8"><strong>Ready to transform your backyard?</strong> Contact us today and let’s turn your outdoor vision into a functional, beautiful space that adds long-term value to your home.</p>
          </div>
        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
